import { formatInTimeZone } from 'date-fns-tz';
import { GcpParseResult, getGcpMeasurementDates } from '../gcpRequestUploadFiles/parseGcpCsv';

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export type GcpDateModalReason = 'no-date' | 'suspicious-age';

export type GcpServiceDatePlan =
  /** A date was determined and needs no confirmation — send it as "Services Performed On". */
  | { kind: 'send'; date: string }
  /** The pilot must confirm the date in a modal before submitting. */
  | { kind: 'modal'; defaultDate: string; reason: GcpDateModalReason }
  /** No date to send — let the processor stamp "now" (its no-date default). */
  | { kind: 'omit' };

export interface PlanGcpServiceDateParams {
  /** Parse results for every measurement file — freshly uploaded and restored alike. */
  parseResults: Array<GcpParseResult | null | undefined>;
  /** Measurement files picked this session (restored files have no File handle to read). */
  measurementFiles: Array<{ lastModified?: number }>;
  isSelfServiced: boolean;
  /** IANA timezone of the site, used to read a file's lastModified as a calendar day. */
  timezone: string;
  /** The scan mission's scheduled day (YYYY-MM-DD), when this GCP request is tied to a schedule. */
  scheduledDate?: string;
  nowMs: number;
}

/**
 * Decides the GCP placement's "Services Performed On" date and whether the pilot must confirm it.
 * Pure — the caller runs any modal and performs the request.
 *
 *  1. Latest date embedded in the measurement CSVs → authoritative, send it.
 *  2. No CSV date and no session file (e.g. editing a restored request) → omit; the processor stamps "now".
 *  3. No CSV date but a file is present → use the latest lastModified. Send silently if it matches the scheduled
 *     day; otherwise TA pilots always confirm, self-serviced only if the file is > 1 week old.
 */
export function planGcpServiceDate(params: PlanGcpServiceDateParams): GcpServiceDatePlan {
  const { parseResults, measurementFiles, isSelfServiced, timezone, scheduledDate, nowMs } = params;

  const csvDates = getGcpMeasurementDates(parseResults);
  if (csvDates.length) return { kind: 'send', date: csvDates[csvDates.length - 1] };

  if (!measurementFiles.length) return { kind: 'omit' };

  const lastModifieds = measurementFiles.map((f) => f.lastModified).filter((n): n is number => typeof n === 'number');
  const instant = lastModifieds.length ? Math.max(...lastModifieds) : nowMs;
  const defaultDate = formatInTimeZone(new Date(instant), timezone, 'yyyy-MM-dd');

  // The file's own day already agrees with the schedule — no need to ask, whoever the pilot is.
  if (scheduledDate && scheduledDate === defaultDate) return { kind: 'send', date: defaultDate };

  if (!isSelfServiced) return { kind: 'modal', defaultDate, reason: 'no-date' };
  if (nowMs - instant > SEVEN_DAYS_MS) return { kind: 'modal', defaultDate, reason: 'suspicious-age' };
  return { kind: 'send', date: defaultDate };
}
